import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { queryClient, QueryKeys } from "@/api/core";
import { getBaseURL, getCachedAccountToken } from "@/contexts/auth/utils";
import { type AiResult, aiResultObject } from "@/schemas";

interface SubmitAiOpts {
  setConversationId?(id: string): void;
}

interface Params {
  query: string;
  conversationId: string | null;
}

const TEMP_CONVERSATION_KEY = "temp";

function updateConversationCache(
  convId: string,
  updater: (prev: AiResult[]) => AiResult[],
) {
  queryClient.setQueryData<AiResult[]>(
    QueryKeys.aiSearch.conversation(convId),
    (prev) => updater(prev ?? []),
  );
}

function mergeIncomingChunk(
  prev: AiResult[],
  incoming: Partial<AiResult>,
  query: string,
): AiResult[] {
  if (prev.length === 0) {
    return [
      {
        ...(incoming as AiResult),
        query,
        content: incoming.content ?? "",
        images: incoming.images ?? [],
      } as AiResult,
    ];
  }

  const next = [...prev];
  const last = next[next.length - 1];

  if (!last.id && incoming.id) {
    next[next.length - 1] = {
      ...(incoming as AiResult),
      query,
      content: (last.content ?? "") + (incoming.content ?? ""),
    } as AiResult;
    return next;
  }

  next[next.length - 1] = {
    ...last,
    ...incoming,
    content: (last.content ?? "") + (incoming.content ?? ""),
  };

  return next;
}

const messages = defineMessages({
  error: {
    id: "common.error",
    defaultMessage: "Something isn't right. Try reloading the page.",
  },
});

export function useSubmitAiSearch({ setConversationId }: SubmitAiOpts) {
  const intl = useIntl();

  return useMutation({
    mutationKey: QueryKeys.aiSearch.submit,
    mutationFn: async (params: Params) => {
      const accessToken = getCachedAccountToken();
      const baseURL = getBaseURL();

      const response = await fetch(`${baseURL}/api/v1/truth/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          content: params.query,
          stream: true,
          conversation_id: params.conversationId,
        }),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        const message =
          errorResponse?.errors?.[0]?.error_message ||
          intl.formatMessage(messages.error);

        throw new Error(message);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let content = "";
      let finalValue: any = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;

          const jsonStr = trimmed.replace(/^data:\s*/, "");
          if (!jsonStr) continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const convId = params.conversationId || "temp";

            updateConversationCache(convId, (prev) =>
              mergeIncomingChunk(prev, parsed.value, params.query),
            );

            if (parsed.type === "delta") {
              const chunk = parsed.value?.content || "";
              content += chunk;
            } else if (parsed.type === "response") {
              const chunk = parsed.value?.content || "";
              content += chunk;

              finalValue = parsed.value;
            }
          } catch {
            console.warn("Could not parse SSE chunk:", jsonStr);
          }
        }
      }

      if (!finalValue) throw new Error("Missing final response object");

      const fullResult = {
        ...finalValue,
        query: params.query,
        content,
      };

      return aiResultObject.parse(fullResult);
    },
    onMutate(variables) {
      const { query, conversationId } = variables;
      const temp = { query, conversation_id: conversationId } as AiResult;
      if (conversationId) {
        updateConversationCache(conversationId, (prev) => [...prev, temp]);
      } else {
        updateConversationCache(TEMP_CONVERSATION_KEY, () => [temp]);
        setConversationId?.(TEMP_CONVERSATION_KEY);
      }
    },
    onError(error, variables) {
      const convId = variables.conversationId || "temp";
      let message: string;

      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === "string") {
        message = error;
      } else {
        message = String(error);
      }

      updateConversationCache(convId, (prev) =>
        mergeIncomingChunk(prev, { error: message }, variables.query),
      );
    },
    onSuccess(data) {
      // Make sure the final, validated object is present in the conversation cache.
      updateConversationCache(data.conversation_id, (prev) => {
        const index = prev.findIndex((r) => r.id === data.id);
        if (index === -1) return [...prev, data];
        const next = [...prev];

        next[index] = data;
        return next;
      });
      setConversationId?.(data.conversation_id);
      queryClient.removeQueries({
        queryKey: QueryKeys.aiSearch.temporaryConversation(),
        exact: true,
      });
    },
  });
}
